import { toast } from 'sonner'
import {
  useSetActionPriority,
  useUpdateProject,
  type ActionPriorityUpdate,
  type ProjectPriority,
} from '@/api/hooks'
import { ChoicePicker, type PickerChoice } from '@/components/choice-picker'
import { StatusPill } from '@/components/status-pill'
import { formatApiErrorMessage } from '@/lib/api-error'
import { usePicked } from '@/hooks/use-picked'

type ActionPriority = ActionPriorityUpdate['priority']
type AnyPriority = ProjectPriority | ActionPriority

const PRIORITY_LABELS: Record<AnyPriority, string> = {
  high: 'High',
  standard: 'Standard',
  normal: 'Normal',
  low: 'Low',
}

/** High is red, the middle level a quiet gray pill, Low plain text: urgency reads before color. */
export function PriorityTag({ priority }: { priority: AnyPriority }) {
  const label = PRIORITY_LABELS[priority]
  if (priority === 'high') return <StatusPill tone="danger">{label}</StatusPill>
  if (priority === 'low') return <span className="px-2 text-[11px] font-medium text-muted-foreground">{label}</span>
  return <StatusPill tone="neutral">{label}</StatusPill>
}

function choicesFor(levels: readonly AnyPriority[]): PickerChoice[] {
  return levels.map((level) => ({
    value: level,
    label: PRIORITY_LABELS[level],
    render: <PriorityTag priority={level} />,
  }))
}

export const PROJECT_PRIORITY_CHOICES = choicesFor(['high', 'standard', 'low'])
export const ACTION_PRIORITY_CHOICES = choicesFor(['high', 'normal', 'low'])

/** A project's priority; click it to pick Low, Standard or High (USA-1451). */
export function ProjectPriorityPicker({
  projectUuid,
  priority,
  align,
}: {
  projectUuid: string
  priority: ProjectPriority
  align?: 'start' | 'center' | 'end'
}) {
  const update = useUpdateProject(projectUuid)
  const { shown, pick, clear } = usePicked(priority)
  return (
    <ChoicePicker
      title="Project priority"
      label={`Project priority: ${PRIORITY_LABELS[shown]}. Change priority`}
      value={shown}
      choices={PROJECT_PRIORITY_CHOICES}
      pending={update.isPending}
      align={align}
      onSelect={(value) => {
        const next = value as ProjectPriority
        pick(next)
        update.mutate(
          { priority: next },
          {
            onError: (error) => {
              clear()
              toast.error(formatApiErrorMessage('Priority not changed', error))
            },
          },
        )
      }}
    >
      <PriorityTag priority={shown} />
    </ChoicePicker>
  )
}

const EDITABLE_ACTION_SOURCES = new Set<string>([
  'purchase_order',
  'order_line',
  'project_cancellation',
  'customer_communication',
  'manufacturer_data',
  'ap_invoice',
  'warranty_claim',
])

/** An action item's priority; click it to pick High, Normal or Low in place of the one its kind gives it. */
export function ActionPriorityPicker({
  source,
  actionUuid,
  priority,
}: {
  source: string
  actionUuid: string
  priority: ActionPriority
}) {
  const setPriority = useSetActionPriority()
  const { shown, pick, clear } = usePicked(priority)
  if (!EDITABLE_ACTION_SOURCES.has(source)) return <PriorityTag priority={priority} />
  return (
    <ChoicePicker
      title="Action priority"
      label={`Action priority: ${PRIORITY_LABELS[shown]}. Change priority`}
      value={shown}
      choices={ACTION_PRIORITY_CHOICES}
      pending={setPriority.isPending}
      onSelect={(value) => {
        const next = value as ActionPriority
        pick(next)
        setPriority.mutate(
          {
            action_source: source as ActionPriorityUpdate['action_source'],
            action_uuid: actionUuid,
            priority: next,
          },
          {
            onError: (error) => {
              clear()
              toast.error(formatApiErrorMessage('Priority not changed', error))
            },
          },
        )
      }}
    >
      <PriorityTag priority={shown} />
    </ChoicePicker>
  )
}
